import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import StreetviewRoundedIcon from '@mui/icons-material/StreetviewRounded';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { isStreetViewTourStep } from '../../../lib/employerReview/buildFindingEvidenceTourSteps';
import {
  GuideContentCard,
  GuideStepProgress,
  GuideTipBlock,
} from './GuideTourPrimitives';
import { guideTourTokens } from './guidedTourTheme';
import { useEmployerEvidenceTourOptional } from './EmployerEvidenceTourContext';

const VARIANT = 'street-view' as const;

/** Full-width guide strip below the map — separate from the panorama so Google controls stay clear. */
export function StreetViewTourGuide() {
  const theme = useTheme();
  const tokens = guideTourTokens(VARIANT, theme);
  const tour = useEmployerEvidenceTourOptional();
  const active = tour?.activeTour;
  const step = active?.steps[active.stepIndex];

  if (!tour || !active || !step || !isStreetViewTourStep(step)) return null;

  const isLast = active.stepIndex >= active.steps.length - 1;
  const lookFor = step.lookFor ?? [];

  return (
    <Box
      sx={{
        flexShrink: 0,
        bgcolor: tokens.scrim,
        borderTop: '1px solid',
        borderColor: tokens.accentBorder,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: { xs: 1.25, sm: 1.5 },
          py: 0.75,
          borderBottom: '1px solid',
          borderColor: alpha(theme.palette.divider, theme.palette.mode === 'light' ? 1 : 0.7),
          bgcolor: tokens.accentSoft,
        }}
      >
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: 1.25,
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(tokens.accent, 0.12),
            color: tokens.accent,
          }}
        >
          <StreetviewRoundedIcon sx={{ fontSize: 15 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <GuideStepProgress stepIndex={active.stepIndex} totalSteps={active.steps.length} tokens={tokens} />
        </Box>
        <IconButton
          size="small"
          aria-label="Close street view guide"
          onClick={() => tour.closeTour()}
          sx={{ flexShrink: 0, color: 'text.secondary', alignSelf: 'flex-start', mt: 0.25 }}
        >
          <CloseRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Stack>

      <Box sx={{ px: { xs: 1.25, sm: 1.5 }, py: { xs: 1.1, sm: 1.25 } }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1rem', sm: '1.05rem' },
            lineHeight: 1.35,
            color: 'text.primary',
            mb: 0.85,
          }}
        >
          {step.title}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1.15fr 1fr auto' },
            gap: { xs: 0.85, sm: 1, lg: 1.15 },
            alignItems: 'stretch',
          }}
        >
          <GuideContentCard
            label="What to check"
            intro={step.description}
            points={lookFor}
            tokens={tokens}
          />

          {step.tip ? (
            <GuideTipBlock label="Tip — compare size" text={step.tip} tokens={tokens} />
          ) : null}

          <Stack
            direction={{ xs: 'row', lg: 'column' }}
            spacing={0.85}
            justifyContent={{ xs: 'flex-start', lg: 'center' }}
            alignItems={{ xs: 'center', lg: 'stretch' }}
            sx={{ flexShrink: 0, minWidth: { lg: 128 } }}
          >
            <Button
              size="medium"
              variant="outlined"
              color="inherit"
              onClick={() => tour.prevStep()}
              disabled={active.stepIndex === 0}
              sx={{
                minWidth: { xs: 88, lg: '100%' },
                fontWeight: 600,
                borderRadius: 2,
                borderColor: tokens.accentBorder,
                color: 'text.secondary',
              }}
            >
              Back
            </Button>
            <Button
              size="medium"
              variant="contained"
              onClick={() => {
                if (isLast) tour.closeTour();
                else tour.nextStep();
              }}
              sx={{
                minWidth: { xs: 108, lg: '100%' },
                fontWeight: 600,
                borderRadius: 2,
                bgcolor: tokens.accent,
                '&:hover': { bgcolor: alpha(tokens.accent, 0.88) },
              }}
            >
              {isLast ? 'Done' : 'Next →'}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
